import { memo, useMemo, useRef } from "react";
import { GifPreview } from "./GifPreview";
import { GifType, IGif, INicheFeedbackResponse, TAddFeedback } from "@/common/services/types";
import { usePlayerObserver } from "./hooks/usePlayerObserver";
import { clsx } from "clsx";
import { useGifMetrics } from "./hooks/useGifMetrics";
import { useLikesData } from "./hooks/useLikesData";
import { useAppStore } from "@/common/store/appStore";
import { useUsersStore } from "@/common/store/storeUsers";

interface IGifPreviewContainerProps {
  gif: IGif;
  embedded?: boolean;
  hideGif: (id: string) => void;
  rel?: string;
  position: number;
  addNicheFeedback?: TAddFeedback;
  nicheFeedbacks?: INicheFeedbackResponse[];
}

const GifPreviewContainerComponent = (props: IGifPreviewContainerProps) => {
  const { hideGif, position, rel, addNicheFeedback, nicheFeedbacks } = props;
  const playerWrapRef = useRef<HTMLDivElement>(null);

  const gif = props.gif;
  const user = useUsersStore(state => state.getUser(gif.userName));

  const { isVisible, isActive } = usePlayerObserver({ playerWrapRef, position, gifId: gif.id });
  const { sendGifView, playCount, incrementPlayCount } = useGifMetrics({ position });
  const isFullScreen = useAppStore((state) => state.isFullScreen);

  useLikesData();

  const { height, isVertical } = useMemo(() => {
    const ratio = gif.width / gif.height;
    const height = ratio < 1.0 ? gif.height : gif.width;
    return { height, isVertical: ratio < 1.0 };
  }, [gif.width, gif.height]);

  return (
    <div
      id={`gif_${gif.id}`}
      className={
        clsx({
          "GifPreview": true,
          "GifPreview_isActive": isVisible,
          "GifPreview_isGallery": !!gif.gallery,
          "GifPreview_isImage": isActive && gif.type === GifType.Image,
          "GifPreview_isVideo": isActive && gif.type === GifType.Video,
          "GifPreview_isWithRepost": addNicheFeedback && nicheFeedbacks,
          "GifPreview_isVertical": isVertical,
        })
      }
      style={{ aspectRatio: isFullScreen ? undefined : `${gif.width} / ${height}` }}
      ref={playerWrapRef}
    >
      { (isActive || isVisible) && (
        <GifPreview
          gif={gif}
          user={user}
          rel={rel}
          position={position}
          wrapRef={playerWrapRef}
          isVisible={isVisible}
          hideGif={hideGif}
          sendGifView={sendGifView}
          playCount={playCount}
          incrementPlayCount={incrementPlayCount}
          addNicheFeedback={addNicheFeedback}
          nicheFeedbacks={nicheFeedbacks}
        />
      )}
    </div>
  );
};

export const GifPreviewContainer = memo(GifPreviewContainerComponent);
